"use client";

import { useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { DesignFeedback } from "@/components/DesignFeedback";
import { DesignGuidance } from "@/components/DesignGuidance";
import { ShareDesign } from "@/components/ShareDesign";
import type { GeneratedDesign } from "@/lib/pipeline/engine";
import { applyPaywallToItems, swapDesignItem, calculateTotalLookPrice } from "@/lib/pipeline/unlock";
import type { Product } from "@/types";
import { formatGBP } from "@/lib/utils";
import type { RoomAccess } from "../use-room-access";
import { ShoppingList } from "./ShoppingList";
import { ShoppingListUnlock } from "./ShoppingListUnlock";

export function DesignReveal({ design, isUnlocked, roomLink, onAccessChange, onDesignChange, onRestart }: {
  design: GeneratedDesign;
  isUnlocked: boolean;
  roomLink: string;
  onAccessChange: (access: RoomAccess) => void;
  onDesignChange: (design: GeneratedDesign) => void;
  onRestart: () => void;
}) {
  const [linkStatus, setLinkStatus] = useState("");
  const displayedItems = useMemo(() => design.status === "ready" ? applyPaywallToItems(design.items, isUnlocked) : [], [design, isUnlocked]);
  function handleItemSwap(itemId: string, replacement: Product) {
    const items = swapDesignItem(design.items, itemId, replacement);
    onDesignChange({ ...design, items, total_price_gbp: calculateTotalLookPrice(items) });
  }

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge variant={isUnlocked ? "sage" : "clay"} className="mb-2">
            {isUnlocked ? "Full Shopping List Unlocked" : "Launch Preview · Hero Items Locked"}
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight">
            Your Shoppable {design.room_type === "bedroom" ? "Bedroom" : "Living Room"}
          </h1>
          <p className="text-sm text-ink-soft mt-1">
            Shop the selected products. Confirm current prices, sizes and availability with each retailer.
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-center">
        {design.status === "ready" && roomLink && <ShareDesign key={design.id} roomLink={roomLink} roomType={design.room_type} />}
        <Button
          variant="outline"
          size="sm"
          onClick={onRestart}
          className="gap-1.5 self-start sm:self-auto"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Start another room</span>
        </Button>
        </div>
      </div>

      {roomLink && <div className="p-4 rounded-xl border border-line space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="font-semibold">Keep this room</h2>
            <p className="text-xs text-ink-soft">Anyone with this private link can open your room and shopping list.</p></div>
          <Button variant="outline" size="sm" onClick={async () => {
            try { await navigator.clipboard.writeText(roomLink); setLinkStatus("Private room link copied."); }
            catch { setLinkStatus("Select the link below to copy it."); }
          }}>Copy private room link</Button>
        </div>
        <input aria-label="Private room link" readOnly value={roomLink} onFocus={event => event.target.select()}
          className="w-full min-w-0 px-3 py-2 text-xs bg-paper-deep border border-line rounded-lg" />
        {linkStatus && <p role="status" className="text-xs text-ink-soft">{linkStatus}</p>}
      </div>}

      <div className="space-y-4">
        <BeforeAfterSlider
          beforeImage={design.before_image}
          afterImage={design.after_image}
          tags={displayedItems.filter(item => item.render_in_image !== false && item.bbox.w > 0 && item.bbox.h > 0)}
          defaultPosition={50}
          imageAspectRatio={design.aspect_ratio}
        />
        <DesignGuidance notes={design.notes} />
        {design.status === "ready" && <DesignFeedback key={design.id} designId={design.id} initialFeedback={design.feedback} />}
      </div>

      <div className="p-6 rounded-2xl bg-paper-deep border border-line shadow-soft flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">
            Estimated Shopping Total ({design.items.length} product lines)
          </span>
          <div className="text-3xl font-mono font-bold text-brass-deep mt-0.5">
            {formatGBP(design.total_price_gbp)}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant="sage">Check availability at retailer</Badge>
        </div>
      </div>

      <ShoppingListUnlock designId={design.id} isUnlocked={isUnlocked} onUnlocked={onAccessChange} setLinkStatus={setLinkStatus} />
      <ShoppingList items={displayedItems} isUnlocked={isUnlocked} onSwapItem={handleItemSwap} />
    </div>
  );
}
